import { useEffect, useCallback } from 'react';
import { X, Keyboard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { KeyboardShortcut } from '@/hooks/useKeyboardShortcuts';

export interface KeyboardShortcutsHelpProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** List of shortcuts to display */
  shortcuts: KeyboardShortcut[];
  /** Optional title for the modal */
  title?: string;
  /** Optional className for styling */
  className?: string;
}

/**
 * Format a keyboard shortcut for display
 */
function formatShortcut(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];

  if (shortcut.ctrlKey || shortcut.metaKey) {
    parts.push('Ctrl');
  }
  if (shortcut.shiftKey) {
    parts.push('Shift');
  }
  if (shortcut.altKey) {
    parts.push('Alt');
  }

  // Format the key nicely
  let key = shortcut.key;
  if (key === ' ') key = 'Space';
  if (key === 'Escape') key = 'Esc';
  if (key === 'Enter') key = 'Enter';
  if (key === 'ArrowUp') key = 'Up';
  if (key === 'ArrowDown') key = 'Down';
  if (key === 'ArrowLeft') key = 'Left';
  if (key === 'ArrowRight') key = 'Right';

  parts.push(key);

  return parts.join(' + ');
}

/**
 * Keyboard shortcut badge component
 */
function ShortcutBadge({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      className={cn(
        'inline-flex items-center justify-center',
        'min-w-[1.75rem] px-1.5 py-0.5',
        'text-xs font-mono font-medium',
        'bg-muted border border-border rounded',
        'text-muted-foreground'
      )}
    >
      {children}
    </kbd>
  );
}

/**
 * Modal component showing keyboard shortcuts help
 */
export function KeyboardShortcutsHelp({
  isOpen,
  onClose,
  shortcuts,
  title = 'Keyboard Shortcuts',
  className,
}: KeyboardShortcutsHelpProps) {
  // Handle click outside to close
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Group shortcuts by category
  const navigationShortcuts = shortcuts.filter((s) =>
    ['j', 'k', 'e'].includes(s.key.toLowerCase())
  );
  const actionShortcuts = shortcuts.filter((s) =>
    ['a', 'm', 'r'].includes(s.key.toLowerCase())
  );
  const editShortcuts = shortcuts.filter(
    (s) => s.key === 'Enter' || s.key === 'Escape'
  );
  const helpShortcuts = shortcuts.filter((s) => s.key === '?');

  return (
    <div
      className={cn(
        'fixed inset-0 z-50',
        'flex items-center justify-center',
        'bg-black/50 backdrop-blur-sm',
        'animate-in fade-in duration-200'
      )}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-shortcuts-title"
    >
      <Card
        className={cn(
          'w-full max-w-lg mx-4',
          'animate-in zoom-in-95 duration-200',
          className
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle
            id="keyboard-shortcuts-title"
            className="flex items-center gap-2 text-lg"
          >
            <Keyboard className="h-5 w-5" />
            {title}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Navigation Section */}
          {navigationShortcuts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Navigation
              </h3>
              <div className="space-y-2">
                {navigationShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm">{shortcut.description}</span>
                    <ShortcutBadge>{formatShortcut(shortcut)}</ShortcutBadge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions Section */}
          {actionShortcuts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Actions
              </h3>
              <div className="space-y-2">
                {actionShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm">{shortcut.description}</span>
                    <ShortcutBadge>{formatShortcut(shortcut)}</ShortcutBadge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Editing Section */}
          {editShortcuts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Editing
              </h3>
              <div className="space-y-2">
                {editShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm">{shortcut.description}</span>
                    <ShortcutBadge>{formatShortcut(shortcut)}</ShortcutBadge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Help Section */}
          {helpShortcuts.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Help
              </h3>
              <div className="space-y-2">
                {helpShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm">{shortcut.description}</span>
                    <ShortcutBadge>{formatShortcut(shortcut)}</ShortcutBadge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer hint */}
          <p className="text-xs text-muted-foreground text-center pt-2 border-t">
            Press <ShortcutBadge>Esc</ShortcutBadge> to close this dialog
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
